import { EyeNoneIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import { Table } from '@radix-ui/themes'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import {
  ScatterPlotColorSwatch,
  ScatterPlotCurrentValue,
  ScatterPlotDescriptionText,
  ScatterPlotEmptyStateContainer,
  ScatterPlotEmptyStateDescription,
  ScatterPlotEmptyStateTitle,
  ScatterPlotNameCell,
  ScatterPlotSensorId,
  ScatterPlotTableContainer,
  ScatterplotTableRoot,
  ScatterPlotValueInfoCell,
  ScatterPlotValueWrapper,
} from 'features/timeSeries/sharedStyles'
import {
  formatCurrentValue,
  formatXValue,
  useSensorTableData,
} from 'features/timeSeries/utils/scatterPlotTableHelpers'
import React from 'react'
import styled from 'styled-components'

const CompactRow = styled.div<{ $isHighlighted?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border}1a;
  cursor: pointer;
  background-color: ${({ $isHighlighted, theme }) =>
    $isHighlighted ? theme.forecast.boxShadow : undefined};

  &:last-child {
    border-bottom: none;
  }
`

const StyledRow = styled(Table.Row)<{ $isHighlighted?: boolean }>`
  min-height: 2rem;
  cursor: pointer;
  background-color: ${({ $isHighlighted, theme }) =>
    $isHighlighted ? theme.forecast.boxShadow : undefined};
`

const StyledValueInfoCell = styled(ScatterPlotValueInfoCell)<{
  $isHidden?: boolean
}>`
  opacity: ${({ $isHidden }) => ($isHidden ? 0.4 : 1)};
`

const StyledNameCell = styled(Table.Cell)<{ $isHidden?: boolean }>`
  flex: 1;
  opacity: ${({ $isHidden }) => ($isHidden ? 0.4 : 1)};
`

const StyledValueCell = styled(Table.Cell)`
  min-width: 4.5rem;
  width: 1%;
  white-space: nowrap;
`

const IconContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: ${({ theme }) => theme.forecast.defaultText};
  opacity: 0.6;

  min-width: 1rem;
  height: 100%;
  &:hover {
    opacity: 1;
    background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  }
`

interface ScatterPlotTableLegendProps {
  isCompact?: boolean
}

export const ScatterPlotTableLegend: React.FC<ScatterPlotTableLegendProps> = ({
  isCompact = false,
}) => {
  const {
    selectedSensors,
    sensorRanges,
    customAxisRanges,
    derivedValues,
    customSensorColors,
    sensorVisibilityConfig,
    setSensorVisibilityConfig,
    xAxisSensorId,
    unitMapping,
    sensorEntries,
    highlightedSensorId,
    setHighlightedSensorId,
  } = useScatterplotContext()

  const { cursorData } = useCursorData()
  const data = useSensorTableData(
    selectedSensors,
    derivedValues,
    sensorRanges,
    customAxisRanges,
    cursorData,
    customSensorColors,
    xAxisSensorId,
    unitMapping,
    sensorEntries
  )

  const toggleVisibility = (itemId: string) => {
    setSensorVisibilityConfig((prev) => ({
      ...prev,
      [itemId]: !(prev[itemId] ?? true),
    }))
  }

  const handleHighlight = (itemId: string) => {
    setHighlightedSensorId((prev) => (prev === itemId ? null : itemId))
  }

  // Show empty state if no data
  if (data.length === 0) {
    const hasConfiguredData =
      selectedSensors.length > 0 || derivedValues.length > 0

    return (
      <ScatterPlotTableContainer>
        <ScatterPlotEmptyStateContainer>
          <ScatterPlotEmptyStateTitle>No data</ScatterPlotEmptyStateTitle>
          <ScatterPlotEmptyStateDescription>
            {hasConfiguredData
              ? 'Loading data for the plot...'
              : 'Use the Configure button to add data to the plot.'}
          </ScatterPlotEmptyStateDescription>
        </ScatterPlotEmptyStateContainer>
      </ScatterPlotTableContainer>
    )
  }

  if (isCompact) {
    return (
      <ScatterPlotTableContainer>
        {data.map((item) => (
          <CompactRow
            key={item.id}
            onClick={() => handleHighlight(item.id)}
            $isHighlighted={highlightedSensorId === item.id}>
            <StyledValueInfoCell
              $isHidden={sensorVisibilityConfig[item.id] === false}>
              <ScatterPlotColorSwatch color={item.color} />
              <ScatterPlotValueWrapper>
                <ScatterPlotCurrentValue>
                  {formatCurrentValue(item.currentValue, item.unit).map(
                    (str, i) => (
                      <span key={i}>{str}</span>
                    )
                  )}
                </ScatterPlotCurrentValue>
                <ScatterPlotCurrentValue $size={'small'}>
                  {formatXValue(item.xValue, item.xUnit)}
                </ScatterPlotCurrentValue>
              </ScatterPlotValueWrapper>
            </StyledValueInfoCell>
            <IconContainer
              onClick={(e) => {
                e.stopPropagation()
                toggleVisibility(item.id)
              }}>
              {sensorVisibilityConfig[item.id] === false ? (
                <EyeNoneIcon />
              ) : (
                <EyeOpenIcon />
              )}
            </IconContainer>
          </CompactRow>
        ))}
      </ScatterPlotTableContainer>
    )
  }

  return (
    <ScatterPlotTableContainer>
      <ScatterplotTableRoot>
        <Table.Body>
          {data.map((item) => (
            <StyledRow
              key={item.id}
              onClick={() => handleHighlight(item.id)}
              $isHighlighted={highlightedSensorId === item.id}>
              <StyledNameCell
                $isHidden={sensorVisibilityConfig[item.id] === false}>
                <ScatterPlotNameCell>
                  <ScatterPlotSensorId>{item.name}</ScatterPlotSensorId>
                  <ScatterPlotDescriptionText>
                    {item.description}
                  </ScatterPlotDescriptionText>
                </ScatterPlotNameCell>
              </StyledNameCell>
              <StyledValueCell>
                <ScatterPlotValueInfoCell>
                  <ScatterPlotColorSwatch color={item.color} />
                  <ScatterPlotValueWrapper>
                    <ScatterPlotCurrentValue>
                      {formatCurrentValue(item.currentValue, item.unit).map(
                        (str, i) => (
                          <span key={i}>{str}</span>
                        )
                      )}
                    </ScatterPlotCurrentValue>
                    <ScatterPlotCurrentValue $size={'small'}>
                      {formatXValue(item.xValue, item.xUnit)}
                    </ScatterPlotCurrentValue>
                  </ScatterPlotValueWrapper>
                </ScatterPlotValueInfoCell>
              </StyledValueCell>
              <Table.Cell>
                <IconContainer
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleVisibility(item.id)
                  }}>
                  {sensorVisibilityConfig[item.id] === false ? (
                    <EyeNoneIcon />
                  ) : (
                    <EyeOpenIcon />
                  )}
                </IconContainer>
              </Table.Cell>
            </StyledRow>
          ))}
        </Table.Body>
      </ScatterplotTableRoot>
    </ScatterPlotTableContainer>
  )
}
